/**
 * Product Categories Hook
 * 
 * Manages product categories used for organizing and classifying products/services
 * across the platform. Categories help users navigate the marketplace and enable
 * targeted search and filtering.
 * 
 * **Category Management:**
 * - Hierarchical organization of products
 * - Display order control for UI presentation
 * - Active/inactive status for lifecycle management
 * - Icon and description support for rich display
 * 
 * **Key Features:**
 * - Fetch all categories (active only or including inactive)
 * - Create new product categories
 * - Update existing categories (name, description, icon, order)
 * - Delete categories (hard delete)
 * - Deactivate categories (soft delete for data integrity)
 * - Manual refetch capability
 * 
 * **Category Lifecycle:**
 * 1. **Active**: Visible in UI, available for assignment
 * 2. **Inactive**: Hidden from UI, existing assignments preserved
 * 3. **Deleted**: Permanently removed (use with caution)
 * 
 * **Use Cases:**
 * - RFQ category selection
 * - Vendor capability classification
 * - Product catalog organization
 * - Search and filter facets
 * - Admin category management
 * 
 * **Best Practices:**
 * - Prefer deactivation over deletion to maintain historical data
 * - Use meaningful display_order for intuitive navigation
 * - Include descriptive icons for visual recognition
 * - Keep category names concise but descriptive
 * 
 * @param {boolean} includeInactive - Whether to fetch inactive categories
 * 
 * @returns {Object} Product categories state
 * @returns {ProductCategory[]} categories - Array of product categories
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} createCategory - Create new category
 * @returns {Function} updateCategory - Update existing category
 * @returns {Function} deleteCategory - Permanently delete category
 * @returns {Function} deactivateCategory - Soft delete category
 * @returns {Function} refetch - Manually refresh categories
 * 
 * @example
 * // Fetch active categories only
 * const { categories, loading } = useProductCategories();
 * 
 * categories.map(cat => (
 *   <CategoryCard
 *     key={cat.id}
 *     name={cat.name}
 *     icon={cat.icon}
 *   />
 * ));
 * 
 * @example
 * // Admin view with inactive categories
 * const {
 *   categories,
 *   createCategory,
 *   updateCategory,
 *   deactivateCategory
 * } = useProductCategories(true);
 * 
 * // Create new category
 * await createCategory({
 *   name: 'Electronics',
 *   description: 'Electronic components and devices',
 *   icon: 'chip',
 *   display_order: 10
 * });
 * 
 * @example
 * // Reorder and update
 * await updateCategory(categoryId, {
 *   display_order: 5,
 *   description: 'Updated description'
 * });
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface ProductCategory {
  id: string;
  public_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  display_order: number;
  is_active: boolean;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateCategoryData {
  name: string;
  description?: string;
  icon?: string;
  display_order: number;
}

interface UpdateCategoryData {
  name?: string;
  description?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
}

export const useProductCategories = (includeInactive = false) => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('product_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setCategories(data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch product categories';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [includeInactive]);

  const createCategory = async (categoryData: CreateCategoryData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: createError } = await supabase
        .from('product_categories')
        .insert({
          ...categoryData,
          created_by_user_id: user.id,
        })
        .select()
        .single();

      if (createError) throw createError;

      toast({
        title: 'Success',
        description: 'Product category created successfully',
      });

      await fetchCategories();
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create product category';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateCategory = async (id: string, updates: UpdateCategoryData) => {
    try {
      const { error: updateError } = await supabase
        .from('product_categories')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Product category updated successfully',
      });

      await fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update product category';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('product_categories')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'Product category deleted successfully',
      });

      await fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete product category';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deactivateCategory = async (id: string) => {
    await updateCategory(id, { is_active: false });
  };

  return {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    deactivateCategory,
    refetch: fetchCategories,
  };
};
